import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBackOutline, IoSearch } from "react-icons/io5";
import axios from "axios";
import "../css/Charts.css";
import "../css/mainhome.css";

const API_BASE_URL = "https://admin.gama567.club/api/v1";
const VALID_CHART_TYPES = new Set(["game", "starline", "jackpot"]);

function normalizeChart(chart) {
  const gameId = Number(chart?.gameId);
  const gameName = String(chart?.gameName ?? "").trim();
  const rawType = String(chart?.gameType ?? "game").toLowerCase();

  if (!Number.isInteger(gameId) || gameId <= 0 || !gameName) {
    return null;
  }

  return {
    gameId,
    gameName,
    gameType: VALID_CHART_TYPES.has(rawType) ? rawType : "game",
  };
}

function createSlug(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "chart";
}

export default function ChartsScreen() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const fetchCharts = async () => {
      setLoading(true);
      setError("");

      try {
        const headers = {
          "Content-Type": "application/json",
          deviceId: "qwert",
          deviceName: "sm2233",
          accessStatus: "1",
        };

        const response = await axios.get(`${API_BASE_URL}/chart-game-list`, {
          headers,
          signal: controller.signal,
        });

        if (response.data?.status !== true || !Array.isArray(response.data.info)) {
          throw new Error(response.data?.msg || "Failed to fetch chart list.");
        }

        const uniqueCharts = new Map();
        response.data.info.forEach((item) => {
          const chart = normalizeChart(item);
          if (chart) {
            uniqueCharts.set(`${chart.gameType}:${chart.gameId}`, chart);
          }
        });

        if (active) {
          setCharts(Array.from(uniqueCharts.values()));
        }
      } catch (err) {
        if (!active || axios.isCancel(err)) return;
        setCharts([]);
        setError(err.response?.data?.msg || err.message || "Network error. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchCharts();
    return () => {
      active = false;
      controller.abort();
    };
  }, [retryCount]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredCharts = charts.filter(chart =>
    chart.gameName.toLowerCase().includes(normalizedQuery)
  );

  return (
    <div className="mainhome-screen-wrapper">
      <div className="charts-header">
        <button className="back-btn" onClick={() => navigate(-1)} aria-label="Back">
          <IoArrowBackOutline size={24} />
        </button>
        <h2>Charts</h2>
      </div>

      <div className="search-box">
        <IoSearch size={20} className="search-icon" />
        <input
          type="text"
          placeholder="Search chart"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      <div className="chart-grid screen-content">
        {loading && <p className="chart-message">Loading charts...</p>}
        {!loading && error && (
          <div className="chart-message-panel">
            <p className="chart-message error" role="alert">{error}</p>
            <button type="button" className="chart-retry-btn" onClick={() => setRetryCount(value => value + 1)}>
              Try again
            </button>
          </div>
        )}
        {!loading && !error && filteredCharts.length === 0 && (
          <p className="chart-message">No charts found.</p>
        )}
        {!loading && !error && (
          filteredCharts.map(chart => (
            <button
              key={`${chart.gameType}-${chart.gameId}`}
              className="chart-btn"
              onClick={() => navigate(`/charts/${chart.gameType}/${chart.gameId}/${createSlug(chart.gameName)}`)}
            >
              {chart.gameName}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
